import { Circle, Image as ImageIcon, Square } from 'lucide-react';

import { AdComponent, base, margin, shape, unit } from './shared';

/**
 * Logo marki w trzech oprawach.
 *
 * Sygnet na zdjęciu albo na ciemnym tle bywa nieczytelny, dlatego poza samym
 * logo dajemy dwa warianty z białą podkładką. Podkładka jest osobną warstwą,
 * więc da się jej zmienić kolor bez ruszania logo.
 */
/** Proporcje pełnego logo Hotelspota (88 x 32 w pliku źródłowym). */
const WORDMARK_RATIO = 32 / 88;

export const BRAND_COMPONENTS: AdComponent[] = [
  {
    id: 'wordmark',
    label: 'Logo Hotelspot',
    description: 'Pełne logo z napisem',
    icon: ImageIcon,
    build: ({ scene, wordmarkUrl }) => {
      const width = Math.round(scene.width * 0.32);

      return [
        {
          ...base('Logo Hotelspot'),
          type: 'image',
          src: wordmarkUrl,
          x: margin(scene),
          y: Math.round(scene.height * 0.1),
          width,
          height: Math.round(width * WORDMARK_RATIO),
          radius: 0,
          fit: 'contain',
          circle: false,
        },
      ];
    },
  },
  {
    id: 'logo',
    label: 'Sygnet Hotelspot',
    description: 'Sam znak marki, bez podkładki',
    icon: ImageIcon,
    build: ({ scene, logoUrl }) => {
      const size = Math.round(unit(scene) * 0.12);

      return [
        {
          ...base('Logo marki'),
          type: 'image',
          src: logoUrl,
          x: margin(scene),
          y: Math.round(scene.height * 0.1),
          width: size,
          height: size,
          radius: 0,
          fit: 'contain',
          circle: false,
        },
      ];
    },
  },
  {
    id: 'logo-circle',
    label: 'Sygnet w kole',
    description: 'Znak marki na białym okręgu',
    icon: Circle,
    build: ({ scene, logoUrl }) => {
      const badge = Math.round(unit(scene) * 0.16);
      const logo = Math.round(badge * 0.56);
      const x = margin(scene);
      const y = Math.round(scene.height * 0.1);

      return [
        shape('Podkładka logo', {
          shape: 'ellipse',
          x,
          y,
          width: badge,
          height: badge,
          fill: '#ffffff',
        }),
        {
          ...base('Logo marki'),
          type: 'image',
          src: logoUrl,
          x: x + Math.round((badge - logo) / 2),
          y: y + Math.round((badge - logo) / 2),
          width: logo,
          height: logo,
          radius: 0,
          fit: 'contain',
          circle: false,
        },
      ];
    },
  },
  {
    id: 'logo-square',
    label: 'Sygnet w kwadracie',
    description: 'Znak marki na białym zaokrąglonym kwadracie',
    icon: Square,
    build: ({ scene, logoUrl }) => {
      const badge = Math.round(unit(scene) * 0.16);
      const logo = Math.round(badge * 0.56);
      const x = margin(scene);
      const y = Math.round(scene.height * 0.1);

      return [
        shape('Podkładka logo', {
          x,
          y,
          width: badge,
          height: badge,
          fill: '#ffffff',
          radius: Math.round(badge * 0.24),
        }),
        {
          ...base('Logo marki'),
          type: 'image',
          src: logoUrl,
          x: x + Math.round((badge - logo) / 2),
          y: y + Math.round((badge - logo) / 2),
          width: logo,
          height: logo,
          radius: 0,
          fit: 'contain',
          circle: false,
        },
      ];
    },
  },
  {
    id: 'wordmark-circle',
    label: 'Logo w kole',
    description: 'Pełne logo na białym okręgu',
    icon: Circle,
    build: ({ scene, wordmarkUrl }) => {
      const logoWidth = Math.round(scene.width * 0.26);
      // Koło musi zmieścić szeroki napis, więc liczymy je z szerokości logo,
      // a nie odwrotnie — inaczej logo wychodziłoby poza podkładkę.
      const badge = Math.round(logoWidth * 1.35);
      const x = margin(scene);
      const y = Math.round(scene.height * 0.1);
      const logoHeight = Math.round(logoWidth * WORDMARK_RATIO);

      return [
        shape('Podkładka logo', {
          shape: 'ellipse',
          x,
          y,
          width: badge,
          height: badge,
          fill: '#ffffff',
        }),
        {
          ...base('Logo Hotelspot'),
          type: 'image',
          src: wordmarkUrl,
          x: x + Math.round((badge - logoWidth) / 2),
          y: y + Math.round((badge - logoHeight) / 2),
          width: logoWidth,
          height: logoHeight,
          radius: 0,
          fit: 'contain',
          circle: false,
        },
      ];
    },
  },
  {
    id: 'wordmark-square',
    label: 'Logo w kwadracie',
    description: 'Pełne logo na białym zaokrąglonym kwadracie',
    icon: Square,
    build: ({ scene, wordmarkUrl }) => {
      const logoWidth = Math.round(scene.width * 0.26);
      const badge = Math.round(logoWidth * 1.2);
      const x = margin(scene);
      const y = Math.round(scene.height * 0.1);
      const logoHeight = Math.round(logoWidth * WORDMARK_RATIO);

      return [
        shape('Podkładka logo', {
          x,
          y,
          width: badge,
          height: badge,
          fill: '#ffffff',
          radius: Math.round(badge * 0.18),
        }),
        {
          ...base('Logo Hotelspot'),
          type: 'image',
          src: wordmarkUrl,
          x: x + Math.round((badge - logoWidth) / 2),
          y: y + Math.round((badge - logoHeight) / 2),
          width: logoWidth,
          height: logoHeight,
          radius: 0,
          fit: 'contain',
          circle: false,
        },
      ];
    },
  },
];
